import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Star, Heart, Loader2 } from "lucide-react";

const empty = { name: "", phone: "", email: "", note: "" };

function ReferralDialog({ open, setOpen }) {
  const [f, setF] = useState(empty);
  const send = useMutation({
    mutationFn: async () => (await api.post("/buyer/referrals", f)).data,
    onSuccess: () => { setOpen(false); setF(empty); toast.success("Mahalo! Marlene will reach out to them personally."); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="referral-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Refer a friend or family member</DialogTitle>
          <DialogDescription>Share who might need help buying or selling. Only Marlene sees this. Please check with them first.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Their name *" value={f.name} onChange={set("name")} data-testid="referral-name-input" />
          <Input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="referral-phone-input" />
          <Input type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="referral-email-input" />
          <Textarea placeholder="Anything Marlene should know? (optional)" value={f.note} onChange={set("note")} className="rounded-xl" data-testid="referral-note-input" />
        </div>
        <DialogFooter>
          <Button onClick={() => send.mutate()} disabled={!f.name.trim() || (!f.phone.trim() && !f.email.trim()) || send.isPending} className="rounded-full" data-testid="referral-submit-btn">
            {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send to Marlene"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReviewsReferrals({ reviewUrl }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="p-6 rounded-2xl bg-primary/5 border-primary/20" data-testid="reviews-referrals-card">
      <p className="font-heading text-lg font-semibold">You did it, welcome home!</p>
      <p className="text-sm text-muted-foreground mt-1">If Marlene made this easier, a review or a referral means the world to a local agent.</p>
      <div className="flex flex-wrap gap-2 mt-4">
        {reviewUrl ? (
          <Button asChild className="rounded-full gap-2" data-testid="leave-review-btn">
            <a href={reviewUrl} target="_blank" rel="noreferrer"><Star className="h-4 w-4" /> Leave a review</a>
          </Button>
        ) : (
          <Button disabled className="rounded-full gap-2" data-testid="leave-review-btn"><Star className="h-4 w-4" /> Review link coming soon</Button>
        )}
        <Button variant="outline" className="rounded-full gap-2" onClick={() => setOpen(true)} data-testid="refer-friend-btn"><Heart className="h-4 w-4" /> Refer a friend</Button>
      </div>
      <ReferralDialog open={open} setOpen={setOpen} />
    </Card>
  );
}
